import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEventDocuments } from 'src/api/company';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/documents/$documentId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({ all: 'yes', includeLogs: 'true' });
	const { data: documents, isLoading: docsLoading } = useQuery({
		queryKey: ['company-event-documents', groupId, eventId, 'all'],
		queryFn: () => getCompanyEventDocuments(groupId, eventId, query),
		enabled: !userLoading,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['company-document-types-events'],
		queryFn: () => getDocumentTypes('events'),
	});

	useBreadcrumbLevel(3, 'Documents', `/company/${groupId}/events/${eventId}/documents`);
	useBreadcrumbLevel(4, 'Document Details');

	if (userLoading || docsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser) return null;

	const document = (documents?.data || []).find((d) => d.id === documentId || d._id === documentId);
	if (!document) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	const handleClose = () => {
		navigate({ to: '/company/$groupId/events/$eventId/documents', params: { groupId, eventId } });
	};

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypes?.data || []}
			apiUrlBase={`/api/company-portal/groups/${groupId}/events/${eventId}/files`}
			onRevalidateDocument={() =>
				queryClient.invalidateQueries({ queryKey: ['company-event-documents', groupId, eventId] })
			}
			tasks={[]}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.companyDocumentsView]),
				fullView: verifyPermission([PERMISSIONS.companyDocumentsView]),
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: false,
				archive: false,
				review: false,
				enableReview: false,
			}}
		/>
	);
}
